import * as React from 'react'
import { cn } from '../../lib/utils'

/**
 * The column count rides context so rows and footers span correctly whether
 * or not the table declares the optional entity-kind column.
 */
const DiffTableColumnsContext = React.createContext(3)

/**
 * The shared flat diff table — the visual pattern the gwbk ingest diff
 * established: one scannable `Kind | Type | Entity` row per operation, with a
 * click-to-expand body (property changes, a conflict grid, an error) sliding
 * open in place. Presentation-only: cell content arrives rendered; consumers
 * own decoding, equality, and ordering.
 *
 * `entityKindHeader` adds a narrow node/relationship column between the kind
 * badge and the type; rows in such a table should supply `entityKind`.
 */
export function DiffTable({
  kindHeader = 'Kind',
  entityKindHeader,
  typeHeader = 'Type',
  entityHeader = 'Entity',
  headerless = false,
  bare = false,
  children
}: {
  kindHeader?: string
  /** Header of the node/rel column; omitting it omits the column. */
  entityKindHeader?: string
  typeHeader?: string
  entityHeader?: string
  /** Skip the header row (a table continuing under another's header). */
  headerless?: boolean
  /** Skip the rounded border wrapper (already inside a bordered section). */
  bare?: boolean
  children: React.ReactNode
}) {
  const columns = entityKindHeader !== undefined ? 4 : 3
  const table = (
    <table className="w-full table-fixed text-left text-xs">
      {!headerless ? (
        <thead
          className={cn(
            'text-ink-3',
            bare ? 'border-y border-line bg-bg-sunk/40' : 'border-b border-line bg-bg-sunk/60'
          )}
        >
          <tr>
            <th className="w-20 px-2 py-1.5 text-10 font-medium tracking-wider uppercase">
              {kindHeader}
            </th>
            {entityKindHeader !== undefined ? (
              <th className="w-13 px-2 py-1.5 text-10 font-medium tracking-wider uppercase">
                {entityKindHeader}
              </th>
            ) : null}
            <th className="w-27.5 px-2 py-1.5 text-10 font-medium tracking-wider uppercase">
              {typeHeader}
            </th>
            <th className="px-2 py-1.5 text-10 font-medium tracking-wider uppercase">
              {entityHeader}
            </th>
          </tr>
        </thead>
      ) : null}
      <tbody>{children}</tbody>
    </table>
  )
  const framed = bare ? (
    table
  ) : (
    <div className="overflow-hidden rounded-md border border-line">{table}</div>
  )
  return (
    <DiffTableColumnsContext.Provider value={columns}>{framed}</DiffTableColumnsContext.Provider>
  )
}

/** How long a closing row keeps its body mounted — the grid-rows transition
 * (200ms) plus a small buffer, so the collapse animates over real content. */
const COLLAPSE_UNMOUNT_MS = 250

/**
 * One diff-table row. With `children`, the row is expandable and the children
 * render in an animated body spanning all columns. Expansion is uncontrolled
 * by default; pass `open`/`onOpenChange` to coordinate one-open-at-a-time
 * across a table.
 */
export function DiffRow({
  badge,
  type,
  entityKind,
  entity,
  entityTitle,
  summary,
  open,
  onOpenChange,
  expandable: expandableProp,
  children
}: {
  /** The kind cell — typically a `KindBadge`. */
  badge: React.ReactNode
  /** The type cell — a node label, relationship type, or entity type. */
  type: React.ReactNode
  /**
   * What kind of entity the row is about — "node" / "rel" — filling the
   * table's optional entity-kind column (see `DiffTable.entityKindHeader`).
   */
  entityKind?: string
  /** The entity cell — a display name or id (rendered mono, truncated). */
  entity: React.ReactNode
  /** Hover title for the row (the full URN/id). */
  entityTitle?: string
  /** A trailing muted annotation on the entity cell, e.g. "(3 fields)". */
  summary?: React.ReactNode
  open?: boolean
  onOpenChange?: (open: boolean) => void
  /**
   * Whether the row expands; defaults to the presence of `children`. Pass
   * explicitly when the body is built lazily (only for the open row).
   */
  expandable?: boolean
  /** The expansion body; only rendered while expanded. */
  children?: React.ReactNode
}) {
  const columns = React.useContext(DiffTableColumnsContext)
  const [internal, setInternal] = React.useState(false)
  const expandable = expandableProp ?? children != null
  const expanded = open ?? internal
  const setExpanded = onOpenChange ?? setInternal

  // The body stays mounted through the closing transition so the collapse
  // animates over real content instead of snapping shut the moment the row
  // closes. Mounting on expand is synchronous (render-phase adjustment) so the
  // opening transition never shows an empty frame; unmounting waits out the
  // grid-rows transition.
  const [mounted, setMounted] = React.useState(expanded)
  if (expanded && !mounted) setMounted(true)
  React.useEffect(() => {
    if (expanded || !mounted) return
    const timer = setTimeout(() => setMounted(false), COLLAPSE_UNMOUNT_MS)
    return () => clearTimeout(timer)
  }, [expanded, mounted])

  return (
    <>
      <tr
        title={entityTitle}
        onClick={expandable ? () => setExpanded(!expanded) : undefined}
        className={cn(
          'border-b border-line last:border-b-0 hover:bg-bg-sunk/60',
          expandable && 'cursor-pointer'
        )}
      >
        <td className="px-2 py-1">{badge}</td>
        {columns === 4 ? (
          <td className="px-2 py-1 text-10 tracking-wide uppercase text-ink-3">{entityKind}</td>
        ) : null}
        <td className="truncate px-2 py-1 text-ink-2">{type}</td>
        <td className="truncate px-2 py-1 font-mono text-ink-2">
          <span className="inline-flex max-w-full items-center gap-1">
            {expandable ? (
              <span className="shrink-0 text-ink-3" aria-hidden="true">
                {expanded ? '▾' : '▸'}
              </span>
            ) : null}
            <span className="truncate">{entity}</span>
            {summary != null ? (
              <span className="ml-1 shrink-0 text-10 text-ink-3">{summary}</span>
            ) : null}
          </span>
        </td>
      </tr>
      {expandable ? (
        <tr className={cn('bg-bg-sunk/40', expanded && 'border-b border-line last:border-b-0')}>
          <td colSpan={columns} className="p-0">
            <div
              className={cn(
                'grid transition-[grid-template-rows] duration-200 ease-out',
                expanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
              )}
            >
              <div className="overflow-hidden">
                <div className="px-2 py-2">{mounted ? children : null}</div>
              </div>
            </div>
          </td>
        </tr>
      ) : null}
    </>
  )
}

/** Rows per page in a paginated diff table. */
export const DIFF_TABLE_PAGE_SIZE = 50

/**
 * The pager footer row for a paginated table: the visible range plus
 * First/Prev/Next/Last controls. Hosts own the page state, clamping, and
 * slicing; a set that fits one page renders no footer at all.
 */
export function DiffPagerRow({
  page,
  total,
  pageSize = DIFF_TABLE_PAGE_SIZE,
  onPageChange
}: {
  /** The current page (0-based, already clamped by the host). */
  page: number
  /** Total row count across all pages. */
  total: number
  pageSize?: number
  onPageChange: (page: number) => void
}) {
  const columns = React.useContext(DiffTableColumnsContext)
  if (total <= pageSize) return null
  const pageCount = Math.ceil(total / pageSize)
  const start = page * pageSize + 1
  const end = Math.min(total, (page + 1) * pageSize)
  const canPrev = page > 0
  const canNext = page < pageCount - 1
  return (
    <tr>
      <td colSpan={columns} className="px-2 py-1.5">
        <div className="flex flex-wrap items-center justify-between gap-2 text-11 text-ink-3">
          <span>{`${start.toLocaleString()}–${end.toLocaleString()} of ${total.toLocaleString()}`}</span>
          <div className="flex items-center gap-2">
            <PagerButton onClick={() => onPageChange(0)} disabled={!canPrev}>
              First
            </PagerButton>
            <PagerButton onClick={() => onPageChange(page - 1)} disabled={!canPrev}>
              Prev
            </PagerButton>
            <span>
              Page {page + 1} of {pageCount}
            </span>
            <PagerButton onClick={() => onPageChange(page + 1)} disabled={!canNext}>
              Next
            </PagerButton>
            <PagerButton onClick={() => onPageChange(pageCount - 1)} disabled={!canNext}>
              Last
            </PagerButton>
          </div>
        </div>
      </td>
    </tr>
  )
}

function PagerButton({
  onClick,
  disabled,
  children
}: {
  onClick: () => void
  disabled: boolean
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="cursor-pointer rounded-sm border border-line px-2 py-0.5 hover:text-ink disabled:cursor-default disabled:opacity-50"
    >
      {children}
    </button>
  )
}
